import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { AddCircle, DeleteOutline, PlaylistAdd, Save } from '@mui/icons-material';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, ServiceEntryDetail } from '../../services/procurementService';
import {
  P,
  cardSx,
  fieldSx,
  priBtn,
  outBtn,
  gridInput,
  thSx,
  thTitle,
  PX,
  FieldLabel,
  StatusPill,
  WorkspaceHeader,
  TotalsStrip,
  ValidationStrip,
} from '../../components/common/workspaceKit';

interface ServicePoOption {
  id: number;
  po_number: string;
  vendor_id: string;
  vendor_name: string;
  status: string;
}

interface PoLineSuggestion {
  description: string;
  quantity: number | string;
  uom: string;
  rate: number | string;
}

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

let draftKeySeq = 0;
const nextDraftKey = () => `sesline-${++draftKeySeq}`;

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  uom: string;
  rate: string;
}

const emptyLine = (): LineDraft => ({
  key: nextDraftKey(),
  description: '',
  quantity: '1',
  uom: '',
  rate: '',
});

const ServiceEntryFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const prefillPoId = searchParams.get('poId');
  const prefillSwoId = searchParams.get('swoId');
  const isEdit = !!id;

  const [pos, setPos] = useState<ServicePoOption[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingEntry, setLoadingEntry] = useState(isEdit);

  const [selectedPo, setSelectedPo] = useState<ServicePoOption | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [poLines, setPoLines] = useState<PoLineSuggestion[]>([]);
  const [loadingPoLines, setLoadingPoLines] = useState(false);

  const [servicePeriodFrom, setServicePeriodFrom] = useState(todayISO());
  const [servicePeriodTo, setServicePeriodTo] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    api
      .get<{ purchaseOrders: ServicePoOption[] }>('/procurement/purchase-orders', { params: { poType: 'SERVICE' } })
      .then((r) => setPos(r.purchaseOrders || []))
      .catch(() => setPos([]))
      .finally(() => setLoadingRefData(false));
  }, []);

  const loadPoDetail = useCallback((po: ServicePoOption | null) => {
    setPoLines([]);
    if (!po) return;
    setLoadingPoLines(true);
    api
      .get<{ purchaseOrder: any }>(`/procurement/purchase-orders/${po.id}`)
      .then(({ purchaseOrder }) => {
        setVendorId(purchaseOrder.vendor_id);
        setVendorName(purchaseOrder.vendor_name || po.vendor_name);
        setPoLines(
          (purchaseOrder.items || []).map((it: any) => ({
            description: it.product_name || it.line_description || 'Service',
            quantity: it.quantity,
            uom: it.line_uom || it.unit || '',
            rate: it.unit_price,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingPoLines(false));
  }, []);

  const handleSelectPo = (po: ServicePoOption | null) => {
    setSelectedPo(po);
    setVendorId(po?.vendor_id || '');
    setVendorName(po?.vendor_name || '');
    loadPoDetail(po);
  };

  // Prefill from ?poId= once PO options are loaded (new-entry mode only).
  useEffect(() => {
    if (isEdit || loadingRefData || !prefillPoId || selectedPo) return;
    const match = pos.find((p) => String(p.id) === String(prefillPoId));
    if (match) handleSelectPo(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, loadingRefData, prefillPoId, pos]);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingEntry(true);
    procurementService
      .getServiceEntry(id)
      .then(({ serviceEntry }: { serviceEntry: ServiceEntryDetail }) => {
        setSelectedPo({
          id: serviceEntry.po_id,
          po_number: serviceEntry.po_number,
          vendor_id: serviceEntry.vendor_id,
          vendor_name: serviceEntry.vendor_name,
          status: '',
        });
        setVendorId(serviceEntry.vendor_id);
        setVendorName(serviceEntry.vendor_name);
        setServicePeriodFrom(serviceEntry.service_period_from ? serviceEntry.service_period_from.slice(0, 10) : todayISO());
        setServicePeriodTo(serviceEntry.service_period_to ? serviceEntry.service_period_to.slice(0, 10) : todayISO());
        setDescription(serviceEntry.description || '');
        setRemarks(serviceEntry.remarks || '');
        setStatus(serviceEntry.status);
        setLines(
          (serviceEntry.lines || []).map((l) => ({
            key: nextDraftKey(),
            description: l.description || '',
            quantity: String(l.quantity ?? ''),
            uom: l.uom || '',
            rate: String(l.rate ?? ''),
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load service entry sheet'))
      .finally(() => setLoadingEntry(false));
  }, [isEdit, id]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));
  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addFromPoLine = (suggestion: PoLineSuggestion) => {
    setLines((prev) => [
      ...prev,
      {
        key: nextDraftKey(),
        description: suggestion.description,
        quantity: String(toNum(suggestion.quantity) || 1),
        uom: suggestion.uom,
        rate: String(toNum(suggestion.rate)),
      },
    ]);
  };

  const total = useMemo(() => lines.reduce((sum, l) => sum + toNum(l.quantity) * toNum(l.rate), 0), [lines]);
  const totalQty = useMemo(() => lines.reduce((sum, l) => sum + toNum(l.quantity), 0), [lines]);

  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!selectedPo) errs.push('A service purchase order is required.');
    if (!servicePeriodFrom || !servicePeriodTo) errs.push('Service period from/to is required.');
    if (lines.length === 0) errs.push('At least one service line is required.');
    lines.forEach((l, idx) => {
      if (!l.description.trim()) errs.push(`Line ${idx + 1}: description is required.`);
      if (toNum(l.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
      if (toNum(l.rate) < 0) errs.push(`Line ${idx + 1}: rate cannot be negative.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0 || !selectedPo) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        poId: selectedPo.id,
        serviceWorkOrderId: prefillSwoId ? Number(prefillSwoId) : undefined,
        vendorId,
        servicePeriodFrom,
        servicePeriodTo,
        description: description.trim() || undefined,
        remarks: remarks.trim() || undefined,
        lines: lines.map((l) => ({
          description: l.description.trim(),
          quantity: toNum(l.quantity),
          uom: l.uom.trim() || undefined,
          rate: toNum(l.rate),
        })),
      };

      const { serviceEntry } =
        isEdit && id
          ? await procurementService.updateServiceEntry(id, payload)
          : await procurementService.createServiceEntry(payload);

      navigate(`/procurement/service-entries/${serviceEntry.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save service entry sheet');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingEntry;

  if (loading) {
    return (
      <Box sx={{ bgcolor: P.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const backTo = isEdit && id ? `/procurement/service-entries/${id}` : '/procurement/service-entries';
  const statusLabel = isEdit && status ? status : 'DRAFT';
  const statusTone =
    statusLabel === 'DRAFT' ? 'draft' : statusLabel === 'APPROVED' ? 'green' : statusLabel === 'REJECTED' ? 'red' : statusLabel === 'SUBMITTED' ? 'amber' : 'muted';

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
      <WorkspaceHeader
        onBack={() => navigate(backTo)}
        title={isEdit ? 'Edit Service Entry Sheet' : 'New Service Entry Sheet'}
        subtitle="Record the services actually delivered against a service purchase order, before it can be invoiced"
        chip={<StatusPill label={statusLabel} tone={statusTone as any} />}
        actions={
          canEdit && (
            <>
              <Button variant="outlined" onClick={() => navigate(backTo)} disabled={submitting} sx={outBtn}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Save sx={{ fontSize: 18 }} />}
                onClick={handleSubmit}
                disabled={submitting}
                sx={priBtn}
              >
                {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Service Entry'}
              </Button>
            </>
          )
        }
      />

      <Box sx={{ px: PX, pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            This service entry sheet is {status} and can no longer be edited.
          </Alert>
        )}
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {validationErrors.map((e) => (
              <Box key={e}>{e}</Box>
            ))}
          </Alert>
        )}

        {/* Metadata */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Service Entry Details</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Service Purchase Order" required />
              <Autocomplete
                size="small"
                options={pos}
                value={selectedPo}
                onChange={(_, v) => handleSelectPo(v)}
                getOptionLabel={(o) => `${o.po_number} — ${o.vendor_name}`}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                disabled={!canEdit}
                renderInput={(params) => <TextField {...params} placeholder="Select service PO" sx={fieldSx} />}
              />
            </Box>
            <Box>
              <FieldLabel text="Vendor" />
              <TextField fullWidth size="small" value={vendorName || '—'} disabled sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Service Period From" required />
              <TextField type="date" fullWidth size="small" value={servicePeriodFrom} onChange={(e) => setServicePeriodFrom(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Service Period To" required />
              <TextField type="date" fullWidth size="small" value={servicePeriodTo} onChange={(e) => setServicePeriodTo(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Description" />
              <TextField fullWidth size="small" value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit} placeholder="What was this service entry raised for?" sx={fieldSx} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Remarks" />
              <TextField fullWidth size="small" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canEdit} placeholder="Any additional notes" sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* PO line suggestions — reference only, click Add to copy into service lines */}
        {selectedPo && poLines.length > 0 && canEdit && (
          <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
            <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2, pb: 1.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>PO Lines (for reference — add as service lines)</Typography>
            </Box>
            <Box sx={{ px: { xs: 2, md: 2.75 }, pb: 2 }}>
              {loadingPoLines ? (
                <CircularProgress size={20} />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {poLines.map((pl, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 0.75,
                        borderBottom: idx < poLines.length - 1 ? `1px solid ${P.line}` : 'none',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: P.strong }}>{pl.description}</Typography>
                        <Typography sx={{ fontSize: '12px', color: P.muted }}>
                          {toNum(pl.quantity)} {pl.uom} @ {formatIndianNumber(toNum(pl.rate))}
                        </Typography>
                      </Box>
                      <Button size="small" startIcon={<PlaylistAdd sx={{ fontSize: 18 }} />} onClick={() => addFromPoLine(pl)} sx={{ ...outBtn, height: 34 }}>
                        Add
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Service lines — full width */}
        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${P.line}` }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Service Lines</Typography>
            {canEdit && (
              <Button size="small" startIcon={<AddCircle sx={{ fontSize: 18 }} />} onClick={addLine} sx={{ ...outBtn, height: 34 }}>Add Item</Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 40 }}><Typography sx={thTitle}>#</Typography></TableCell>
                  <TableCell sx={thSx}><Typography sx={thTitle}>Description <Box component="span" sx={{ color: P.red }}>*</Box></Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 110 }}><Typography sx={thTitle}>Qty / Hours</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 90 }}><Typography sx={thTitle}>UOM</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>Rate</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Amount</Typography></TableCell>
                  {canEdit && <TableCell sx={{ ...thSx, width: 48 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, idx) => (
                  <TableRow key={l.key} sx={{ '& td': { borderBottom: `1px solid ${P.tline}`, py: 0.5 } }}>
                    <TableCell sx={{ color: P.muted, fontSize: '13px' }}>{idx + 1}</TableCell>
                    <TableCell>
                      <TextField size="small" value={l.description} onChange={(e) => updateLine(l.key, { description: e.target.value })} disabled={!canEdit} fullWidth placeholder="Service description" sx={gridInput} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={l.quantity} onChange={(e) => updateLine(l.key, { quantity: e.target.value })} inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={l.uom} onChange={(e) => updateLine(l.key, { uom: e.target.value })} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={l.rate} onChange={(e) => updateLine(l.key, { rate: e.target.value })} inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: P.strong, whiteSpace: 'nowrap', fontSize: '13.5px' }}>
                      {formatIndianNumber(toNum(l.quantity) * toNum(l.rate))}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1} sx={{ color: P.muted, '&:hover': { color: P.red } }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Totals + status strip */}
        <Box sx={{ mb: 1.5 }}>
          <TotalsStrip
            cells={[
              { label: 'Line Items', value: lines.length },
              { label: 'Total Quantity', value: formatIndianNumber(totalQty) },
              { label: 'Entry Value', value: `${formatIndianNumber(total)}`, tone: 'primary' },
              { label: 'Status', value: statusLabel },
            ]}
          />
        </Box>

        {/* Validation strip */}
        <ValidationStrip
          items={[
            { ok: !!selectedPo, label: 'Service PO selected' },
            { ok: !!servicePeriodFrom && !!servicePeriodTo, label: 'Service period set' },
            { ok: lines.length > 0, label: 'At least one line' },
          ]}
        />
      </Box>
    </Box>
  );
};

export default ServiceEntryFormPage;
